// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { authService, usersService, subscriptionService, establishmentsService } from 'infrastructure/firebase';
import { isAdminRole } from 'shared/config/roles';

const AuthContext = createContext();

// ─── INVARIANTE DE SEGURANÇA ──────────────────────────────────────────────────
// Super admin é EXCLUSIVAMENTE este e-mail. Nunca derive super admin de
// "admin sem estabelecimentoId" — isso abria escalonamento de privilégio quando
// o cadastro de clínica criava transitoriamente um admin sem estabelecimento.
export const SUPER_ADMIN_EMAIL = 'admin@agendapro.com';

// ─── CICLO DE VIDA DE ACESSO ──────────────────────────────────────────────────
// Mapeia o schema existente (subscriptionStatus + accessExpiresAt) para um status
// efetivo de acesso, sem criar campos/coleções novos:
//   TRIAL  → 'trial'   (ou 'blocked' se vencido)
//   ACTIVE → 'active'  (ou 'blocked' se accessExpiresAt vencido)
//   EXPIRED→ 'blocked' (trial/plano vencido)
//   BLOCKED→ 'suspended' (suspensão manual pelo super admin)
// Retorna: 'active' | 'trial' | 'blocked' | 'suspended'.
function statusFromProfile(profile) {
  if (!profile) return 'active';
  const status = profile.subscriptionStatus;
  const exp    = profile.accessExpiresAt?.toDate?.() ?? null;
  const now    = new Date();
  if (status === 'BLOCKED') return 'suspended';
  if (status === 'EXPIRED') return 'blocked';
  if (status === 'TRIAL') {
    if (exp && exp < now) return 'blocked';
    return 'trial';
  }
  if (status === 'ACTIVE') {
    if (exp && exp < now) return 'blocked';
    return 'active';
  }
  return 'active'; // null/desconhecido = fail-open (não bloqueia indevidamente)
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // Status de acesso efetivo (computado em memória — nunca gravado no banco)
  const [effectiveAccessStatus, setEffectiveAccessStatus] = useState('active');

  // Evita double-processing: quando login()/loginWithGoogle() estão em voo,
  // o listener onAuthChange pula a leitura de perfil (já feita pela função de login).
  const loginInFlightRef = useRef(false);

  // Verifica se o usuário tem acesso expirado e atualiza o status no Firestore.
  // Super admin e clientes não passam por controle de assinatura.
  const checkAndSyncExpiration = async (profile) => {
    // Super admin (somente SUPER_ADMIN_EMAIL) e clientes não têm controle de assinatura.
    const isSuperAdminProfile = isAdminRole(profile.role)
      && (profile.email || '').toLowerCase() === SUPER_ADMIN_EMAIL;
    if (isSuperAdminProfile || profile.role === 'client') return profile;
    if (!profile.accessExpiresAt) return profile;
    if (profile.subscriptionStatus === 'BLOCKED' || profile.subscriptionStatus === 'EXPIRED') return profile;

    const expDate = profile.accessExpiresAt?.toDate?.() ?? null;
    if (expDate && expDate < new Date()) {
      await subscriptionService.markExpired(profile.id).catch(() => {});
      return { ...profile, subscriptionStatus: 'EXPIRED' };
    }
    return profile;
  };

  useEffect(() => {
    // `cancelled` impede que um init() sobrevivente após o cleanup (StrictMode em dev,
    // onde o effect é montado → desmontado → remontado) registre um listener duplicado.
    let cancelled = false;
    let unsubAuth;

    const init = async () => {
      // Timeout de 4s para o check de redirect Google — evita bloqueio por rede lenta
      await Promise.race([
        authService.handleGoogleRedirect?.() ?? Promise.resolve(),
        new Promise(r => setTimeout(r, 4000)),
      ]).catch((err) => {
        console.warn('[Auth] Redirect Google:', err?.code || err?.message);
      });

      // Se o effect foi limpo (StrictMode ou unmount real) antes do await terminar,
      // não registrar o listener — evita dois listeners simultâneos.
      if (cancelled) return;

      unsubAuth = authService.onAuthChange(async (firebaseUser) => {
        // Se login()/loginWithGoogle() estão processando, eles já cuidam do state.
        if (loginInFlightRef.current) {
          setLoading(false);
          return;
        }

        if (firebaseUser) {
          try {
            let profile = await usersService.getById(firebaseUser.uid);

            // Eventual consistency: o perfil pode ter acabado de ser gravado (cadastro
            // por e-mail/senha ou primeiro login Google). getById RETORNA null numa
            // leitura BEM-SUCEDIDA sem documento, e LANÇA em erro de rede/permissão.
            if (!profile) {
              await new Promise(r => setTimeout(r, 1500));
              profile = await usersService.getById(firebaseUser.uid);
            }

            // SEGURANÇA: perfil confirmadamente ausente (leitura ok, documento inexistente).
            // NUNCA assumir um role padrão para um Auth sem perfil — isso já concedeu
            // acesso indevido. Encerra a sessão imediatamente; a guarda manda para /login.
            // (Primeiro login Google cria o doc nos fluxos dedicados loginWithGoogle/
            //  handleGoogleRedirect ANTES do listener disparar, então não há regressão.)
            if (!profile) {
              console.warn('[Auth] Auth sem perfil no Firestore — encerrando sessão (sem fallback de role).');
              await authService.logout().catch(() => {});
              setCurrentUser(null);
              setUserProfile(null);
              setLoading(false);
              return;
            }

            profile = await checkAndSyncExpiration(profile);
            setCurrentUser(firebaseUser);
            setUserProfile(profile);
          } catch (err) {
            // getById LANÇOU: erro de rede/permissão (NÃO é "documento ausente").
            // Não deslogar — pode ser transitório; o Firebase reemite o evento e
            // tentamos de novo. Evita loop de logout em queda de rede.
            console.warn('[Auth] Erro ao carregar perfil (rede?):', err?.message || err?.code);
          }
        } else {
          setCurrentUser(null);
          setUserProfile(null);
        }
        setLoading(false);
      });
    };

    init();
    return () => {
      cancelled = true;
      unsubAuth?.();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    loginInFlightRef.current = true;
    try {
      const cred = await authService.login(email, password);
      let profile = await usersService.getById(cred.user.uid);
      if (!profile) {
        await new Promise(r => setTimeout(r, 1000));
        profile = await usersService.getById(cred.user.uid);
      }
      if (!profile) {
        await authService.logout();
        const err = new Error('PERFIL_NAO_ENCONTRADO');
        err.code = 'auth/profile-not-found';
        throw err;
      }
      profile = await checkAndSyncExpiration(profile);
      setCurrentUser(cred.user);
      setUserProfile(profile);
      return profile;
    } finally {
      loginInFlightRef.current = false;
    }
  };

  const loginWithGoogle = async () => {
    loginInFlightRef.current = true;
    try {
      const user = await authService.loginWithGoogle();
      if (user) {
        let profile = await usersService.getById(user.uid);
        if (!profile) {
          await new Promise(r => setTimeout(r, 1200));
          profile = await usersService.getById(user.uid);
        }
        // SEGURANÇA: nunca assumir um role padrão. O fluxo Google cria o documento em
        // createDocIfNeeded antes de retornar; se mesmo assim o perfil não existe,
        // trata-se de conta inconsistente — encerra a sessão e erra (não vira 'client').
        if (!profile) {
          await authService.logout().catch(() => {});
          const err = new Error('PERFIL_NAO_ENCONTRADO');
          err.code = 'auth/profile-not-found';
          throw err;
        }
        profile = await checkAndSyncExpiration(profile);
        setCurrentUser(user);
        setUserProfile(profile);
        return profile;
      }
      return null;
    } finally {
      loginInFlightRef.current = false;
    }
  };

  const logout = async () => {
    await authService.logout();
    setCurrentUser(null);
    setUserProfile(null);
  };

  // Atualiza assinatura de qualquer usuário (apenas super admin chama isso)
  const updateSubscription = async (uid, status, expiresAt) => {
    await subscriptionService.setAccess(uid, status, expiresAt);
    // Atualiza em memória se for o próprio usuário logado
    if (userProfile?.id === uid) {
      setUserProfile(p => ({ ...p, subscriptionStatus: status, accessExpiresAt: expiresAt }));
    }
  };

  const isAdmin        = isAdminRole(userProfile?.role);
  const isProfessional = userProfile?.role === 'professional';
  const isClient       = userProfile?.role === 'client';

  // Super admin verificado pelo e-mail do TOKEN de auth (currentUser.email),
  // não por campo do Firestore (que poderia ser adulterado) nem por ausência de estab.
  const authEmail      = (currentUser?.email || '').toLowerCase();
  const isSuperAdmin   = isAdmin && authEmail === SUPER_ADMIN_EMAIL;
  // Qualquer admin que NÃO seja o super admin é admin de clínica.
  const isClinicAdmin  = isAdmin && !isSuperAdmin;

  const estabelecimentoId = userProfile?.estabelecimentoId ?? null;

  // ── Cálculo do status de acesso efetivo ────────────────────────────────────
  // Super admin e cliente nunca são bloqueados. Admin de clínica usa o próprio
  // status. Profissional HERDA o status da clínica (se a clínica está bloqueada,
  // o profissional também fica sem acesso). Fail-open em erro de rede.
  useEffect(() => {
    let cancelled = false;

    const compute = async () => {
      if (!userProfile) { setEffectiveAccessStatus('active'); return; }

      // Super admin (e-mail) e clientes: sempre liberados
      if (isSuperAdmin || userProfile.role === 'client') {
        setEffectiveAccessStatus('active');
        return;
      }

      // Admin de clínica: status direto do próprio perfil
      if (isAdminRole(userProfile.role)) {
        setEffectiveAccessStatus(statusFromProfile(userProfile));
        return;
      }

      // Profissional: bloqueio próprio OU herdado da clínica
      if (userProfile.role === 'professional') {
        const own = statusFromProfile(userProfile);
        if (own === 'blocked' || own === 'suspended') {
          setEffectiveAccessStatus(own);
          return;
        }
        if (userProfile.estabelecimentoId) {
          try {
            const estab = await establishmentsService.getById(userProfile.estabelecimentoId);
            if (estab?.adminUid) {
              const owner = await usersService.getById(estab.adminUid);
              const ownerStatus = statusFromProfile(owner);
              if (ownerStatus === 'blocked' || ownerStatus === 'suspended') {
                if (!cancelled) setEffectiveAccessStatus(ownerStatus);
                return;
              }
            }
          } catch {
            // Falha de leitura → fail-open (não bloqueia o profissional por erro de rede)
          }
        }
        if (!cancelled) setEffectiveAccessStatus(own);
        return;
      }

      setEffectiveAccessStatus('active');
    };

    compute();
    return () => { cancelled = true; };
  }, [userProfile, isSuperAdmin]);

  return (
    <AuthContext.Provider value={{
      currentUser, userProfile, loading,
      login, loginWithGoogle, logout,
      updateSubscription,
      isAdmin, isProfessional, isClient,
      isSuperAdmin, isClinicAdmin, estabelecimentoId,
      effectiveAccessStatus,
    }}>
      {loading ? (
        // Spinner global durante inicialização do Firebase Auth
        // Sem isso, {!loading && children} = tela branca até o auth resolver
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: '100vh', background: '#FAF7FB',
          fontFamily: "'DM Sans', sans-serif",
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 44, height: 44, borderRadius: '50%',
              border: '4px solid #E8E5EC', borderTopColor: '#6B5876',
              margin: '0 auto 16px',
              animation: 'auth-spin 0.8s linear infinite',
            }} />
            <div style={{ color: '#A8A1B0', fontSize: 14 }}>Carregando...</div>
            <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 22, color: '#6B5876', marginTop: 16 }}>
              Agenda<span style={{ color: '#B5A2D2' }}>Pro</span>
            </div>
          </div>
          <style>{`@keyframes auth-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
